// Cabinets (all measuring points in scope) and Devices (all VST sensors in
// scope), as in the static demo's pages/Lists.jsx, on real data. Per-row
// history (sparkline, samples in 24 h) is loaded lazily per visible row.
import { useEffect, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { api } from "@gimasi/giothub-shell";
import Icon from "../components/Icon.jsx";
import { Spark } from "../components/Viz.jsx";
import { clsName } from "../data/classes";
import { useT, type T } from "../i18n";
import { useFleet } from "../data/fleet";
import { REGIMES } from "../data/classes";
import { sites, type LocNode, type Point } from "../data/locations";
import { go } from "../lib/nav";
import { AddSensor } from "./AddSensor";
import { State } from "./common";
import { inStock } from "./Stock";
import { accountName, customerOf, multi } from "../lib/account";
import { intervalOf } from "../lib/value";
import { SignalCell, WEAK_RSSI } from "../components/Signal";
import { series as readSeries } from "../lib/series";

const PAGE = 40;
const ORDER: Record<string, number> = { crit: 0, alarm: 1, warn: 2, delayed: 3, defrost: 4, ok: 5, none: 6 };
const KEYS = ["ext_temperature", "temperature_1"];

function Pager({ page, setPage, n, t }: { page: number; setPage: (p: number) => void; n: number; t: T }) {
  return <div class="row" style="justify-content:space-between"><span style="color:var(--muted);font-size:13.5px">{t("list.showing", { a: n ? page * PAGE + 1 : 0, b: Math.min(n, page * PAGE + PAGE), n: t.num(n) })}</span><div class="row"><button class="btn" type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>{t("fp.prev")}</button><button class="btn" type="button" disabled={(page + 1) * PAGE >= n} onClick={() => setPage(page + 1)}>{t("fp.next")}</button></div></div>;
}

/** temperature samples of a point over the last hours (first key the sensor has) */
async function samples(id: string, hours: number) {
  const to = new Date(), from = new Date(to.getTime() - hours * 3600e3);
  for (const key of KEYS) {
    try {
      const s = await readSeries(id, { key, from: from.toISOString(), to: to.toISOString(), resolution: "raw" });
      if (s.items.length) return s.items;
    } catch { /* not on this sensor */ }
  }
  return [];
}

function RowSpark({ p }: { p: Point }) {
  const [d, setD] = useState<number[] | null>(null);
  useEffect(() => {
    let live = true;
    samples(p.id, 6).then((xs) => {
      if (!live) return;
      const bins = Array.from({ length: 24 }, () => [] as number[]), t0 = Date.now() - 6 * 3600e3;
      xs.forEach((x) => { const b = Math.floor((Date.parse(x.utc) - t0) / (15 * 60e3)); if (b >= 0 && b < 24) bins[b].push(x.value); });
      const out: number[] = []; let last: number | null = null;
      bins.forEach((b) => { if (b.length) last = b.reduce((a, v) => a + v, 0) / b.length; if (last != null) out.push(last); });
      setD(out.length > 1 ? out : null);
    });
    return () => { live = false; };
  }, [p.id]);
  return d ? <Spark data={d} color={p.st === "crit" || p.st === "alarm" ? "var(--crit)" : "var(--brand)"} height={28} fill={false} /> : <span style="color:var(--muted)">—</span>;
}

function useRows() {
  const t = useT();
  const f = useFleet(t.lang);
  const siteOf = new Map<string, LocNode>();
  if (f) sites(f.root).forEach((s) => s.points.forEach((p) => siteOf.set(p.id, s)));
  return { t, f, siteOf };
}

export function Cabinets() {
  const { t, f, siteOf } = useRows();
  const [q, setQ] = useState(""); const [fl, setF] = useState("attention"); const [page, setPage] = useState(0);
  if (!f) return <div class="page"><State loading /></div>;
  const rows = f.points.filter((p) => p.kind !== "room");
  const counts = rows.reduce<Record<string, number>>((m, r) => ((m[r.st] = (m[r.st] || 0) + 1), m), {});
  const att = (r: Point) => r.st !== "ok";
  const list = rows.filter((r) => (fl === "all" || (fl === "attention" ? att(r) : r.st === fl)) && (!q || (r.name + " " + (siteOf.get(r.id)?.name || "")).toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => ORDER[a.st] - ORDER[b.st] || (siteOf.get(a.id)?.name || "").localeCompare(siteOf.get(b.id)?.name || ""));
  const shown = list.slice(page * PAGE, page * PAGE + PAGE);
  return (
    <div class="page">
      <div class="page-h"><div><h1>{t("nav.cabinets")}</h1><div class="sub">{t("cabs.sub", { n: t.num(rows.length) })}</div></div></div>
      <div class="row" style="justify-content:space-between">
        <label class="search" style="max-width:420px"><Icon name="search" /><span class="sr">{t("cabs.search")}</span><input value={q} placeholder={t("cabs.search")} onInput={(e) => { setQ((e.target as HTMLInputElement).value); setPage(0); }} /></label>
        <div class="seg">{["attention", "crit", "alarm", "warn", "delayed", "all"].map((k) => <button key={k} type="button" class={fl === k ? "on" : ""} onClick={() => { setF(k); setPage(0); }}>{k === "attention" ? t("cabs.attention") : k === "all" ? t("nav.all") : t("st." + k)}{k !== "all" && k !== "attention" && counts[k] ? ` ${counts[k]}` : ""}</button>)}</div>
      </div>
      {fl === "attention" && list.length === 0 && <p style="color:var(--muted)">{t("dash.allClear")}</p>}
      <section class="card" style="padding:6px 8px"><div style="overflow-x:auto"><table class="t">
        <thead><tr><th>{t("cabs.cabinet")}</th><th>{t("cabs.store")}</th><th>{t("cabs.class")}</th><th class="n">{t("cabs.now")}</th><th>{t("cabs.state")}</th><th style="width:180px">{t("cabs.last6")}</th></tr></thead>
        <tbody>{shown.map((r) => { const s = siteOf.get(r.id); return (
          <tr key={r.id} style="cursor:pointer" onClick={() => go("/cabinets/" + r.id)}>
            <td><b>{r.name}</b><br /><small style="color:var(--muted)">{r.serial}</small></td>
            <td>{s?.name || "—"}<br /><small style="color:var(--muted)">{s?.parent?.parent?.name || ""}</small></td>
            <td>{r.regime ? clsName(t.lang, r.regime) : "—"} <span class="mono" style="color:var(--muted);font-size:12px">{REGIMES[r.regime] ? "" : ""}</span></td>
            <td class="n" style="font-weight:600">{t.temp(r.temp)}</td>
            <td><span class={"st st-" + r.st}>{t("st." + r.st)}</span></td>
            <td><RowSpark p={r} /></td>
          </tr>); })}</tbody>
      </table></div></section>
      <Pager page={page} setPage={setPage} n={list.length} t={t} />
    </div>
  );
}

function Data24({ p }: { p: Point }) {
  const [v, setV] = useState<string>("…");
  useEffect(() => {
    let live = true;
    Promise.all([samples(p.id, 24), api.device(p.id)]).then(([xs, d]) => {
      if (!live) return;
      const iv = intervalOf(d, "sample_interval_s");
      setV(iv > 0 ? `${xs.length} / ${Math.round(86400 / iv)}` : String(xs.length));
    }, () => live && setV("—"));
    return () => { live = false; };
  }, [p.id]);
  return <>{v}</>;
}

export function Devices() {
  const { t, f, siteOf } = useRows();
  const [q, setQ] = useState(""); const [fl, setF] = useState("all"); const [page, setPage] = useState(0);
  const [adding, setAdding] = useState(false);
  if (!f) return <div class="page"><State loading /></div>;
  const rows = f.points;
  const fw = (p: Point) => String((p.labels as Record<string, unknown>).fw_version || "");
  const test: Record<string, (r: Point) => boolean> = { all: () => true, battery: (r) => r.battery != null && r.battery < 25, signal: (r) => r.rssi != null && r.rssi < WEAK_RSSI, delayed: (r) => r.st === "delayed", unbound: (r) => inStock(r), beta: (r) => fw(r).includes("beta") };
  const counts = Object.fromEntries(Object.keys(test).map((k) => [k, rows.filter(test[k]).length]));
  const list = rows.filter((r) => test[fl](r) && (!q || (r.serial + " " + r.name + " " + (siteOf.get(r.id)?.name || "")).toLowerCase().includes(q.toLowerCase())));
  const shown = list.slice(page * PAGE, page * PAGE + PAGE);
  return (
    <div class="page">
      <div class="page-h"><div><h1>{t("nav.devices")}</h1><div class="sub">{t("devs.sub", { n: t.num(rows.length) })}</div></div>
        <div class="row"><button class="btn pri" type="button" onClick={() => setAdding(!adding)}>{t("x.addSensor")}</button></div></div>
      {adding && <AddSensor onDone={(id) => go("/devices/" + id)} />}
      <div class="kpis" style="grid-template-columns:repeat(4,minmax(0,1fr))">
        <div class="card kpi"><span class="lbl">{t("devs.online")}</span><div class="v"><b>{t.num(rows.length - counts.delayed)}</b><span>{t("kpi.of", { n: t.num(rows.length) })}</span></div><span class="foot">{t("devs.onlineNote")}</span></div>
        <div class="card kpi"><span class="lbl">{t("devs.delayed")}</span><div class="v"><b style="color:var(--delayed)">{counts.delayed}</b></div><span class="foot">{t("devs.delayedNote")}</span></div>
        <div class="card kpi"><span class="lbl">{t("devs.battery")}</span><div class="v"><b style="color:var(--warn)">{counts.battery}</b></div><span class="foot">{t("devs.batteryNote")}</span></div>
        <div class="card kpi"><span class="lbl">{t("devs.beta")}</span><div class="v"><b>{counts.beta}</b></div><span class="foot">{t("devs.betaNote")}</span></div>
      </div>
      <div class="row" style="justify-content:space-between">
        <label class="search" style="max-width:420px"><Icon name="search" /><span class="sr">{t("devs.search")}</span><input value={q} placeholder={t("devs.search")} onInput={(e) => { setQ((e.target as HTMLInputElement).value); setPage(0); }} /></label>
        <div class="seg">{Object.keys(test).map((k) => <button key={k} type="button" class={fl === k ? "on" : ""} onClick={() => { setF(k); setPage(0); }}>{t("devs.f." + k)} {k !== "all" ? counts[k] : ""}</button>)}</div>
      </div>
      {rows.length === 0 && <p style="color:var(--muted)">{t("x.noDevices")}</p>}
      <section class="card" style="padding:6px 8px"><div style="overflow-x:auto"><table class="t">
        <thead><tr><th>{t("devs.device")}</th><th>{t("devs.point")}</th><th>{t("cabs.store")}</th>{multi.value && <th>{t("x.customer")}</th>}<th class="n">{t("cab.battery")}</th><th class="n">{t("cab.signal")}</th><th class="n">{t("cab.data24")}</th><th>{t("x.lastRx")}</th><th>{t("devs.fw")}</th></tr></thead>
        <tbody>{shown.map((r) => <tr key={r.id} style="cursor:pointer" onClick={() => go("/devices/" + r.id)}>
          <td><Link href={"/devices/" + r.id}><b class="mono" style="font-size:13.5px">{r.serial}</b></Link><br /><small style="color:var(--muted)">VST · LoRaWAN</small></td>
          <td>{r.name}<br /><small style="color:var(--muted)">{r.regime ? clsName(t.lang, r.regime) : ""}</small></td>
          <td>{inStock(r) ? <span class="st st-defrost">{t("x.lc.inStock")}</span> : siteOf.get(r.id)?.name || "—"}</td>
          {multi.value && <td>{inStock(r) ? "—" : customerOf(r.owner)?.name || accountName(r.owner)}</td>}
          <td class="n">{r.battery == null ? "—" : <span class={"st st-" + (r.battery < 25 ? "warn" : "ok")}>{t.num(r.battery)}%</span>}</td>
          <td class="n"><SignalCell rssi={r.rssi} snr={r.snr} /></td>
          <td class="n">{r.st === "delayed" ? <span class="st st-delayed">{t("st.delayed")}</span> : <Data24 p={r} />}</td>
          <td style="font-size:13px;white-space:nowrap">{r.lastContact ? <><span title={new Date(r.lastContact).toLocaleString(t.locale)}>{t.ago(r.lastContact)}</span><br /><small class="mono" style="color:var(--muted)">{new Date(r.lastContact).toLocaleString(t.locale, { dateStyle: "short", timeStyle: "short" })}</small></> : "—"}</td>
          <td class="mono" style="font-size:13px">{fw(r) || "—"}</td>
        </tr>)}</tbody>
      </table></div></section>
      <Pager page={page} setPage={setPage} n={list.length} t={t} />
    </div>
  );
}
